"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useSkyraSmartScript = useSkyraSmartScript;
var _react = require("react");
var _utils = require("./utils");
var _constants = require("./constants");
var _useMatomo = require("./useMatomo");
/**
 * Skyra smart script lar oss håndtere og samkjøre flere undersøkelser samtidig, uten at brukeren blir bombardert med pop-ups.
 * Dette gjøres ved å sette URL-regler på hvor en undersøkelse kan trigges og hvor den kan følge brukeren (settes i skyra dashboardet, app.skyra.no).
 *
 * I tillegg vil smart scriptet sørge for at en bruker ikke får opp en ny undersøkelse hvis:
 * - Brukeren har avvist en undersøkelse siste 4 timene
 * - Brukeren har fullført en undersøkelse siste 8 timene
 *
 * Om flere undersøkelser kan vises på samme side vil de bli prioritert etter:
 * - Capture rate
 * - Spesifisitet på URL-regler
 * - Undersøkelsen med færrest svar vil prioriteres
 * - Publiseringstidspunkt
 *
 * @param {boolean} [autoStart=true] - Automatisk sett inn scriptet i DOM og start det
 * @param {boolean} [sportTilMatomo=true] - Automatisk spor skyra svar til Matomo
 * @returns {Object}
 * - `startSkyraSmartScript` {function}: Manuelt start scriptet om autoStart=false
 */
function useSkyraSmartScript(props) {
  const {
    autoStart = true,
    sporTilMatomo = true
  } = props || {};
  const {
    leggTilInstruksjon
  } = (0, _useMatomo.useMatomo)();
  const isSessionIdDimensionSetRef = (0, _react.useRef)(false);
  (0, _react.useEffect)(() => {
    if (autoStart) {
      startSkyraSmartScript();
    }
  }, []);
  function startSkyraSmartScript() {
    const alleredeStartet = document.querySelector('script[data-testid="skyra-smart-script"]');
    if (alleredeStartet) {
      return;
    }
    const skyraScript = document.createElement('script');
    skyraScript.src = _utils.skyraScriptURL;
    skyraScript.async = true;
    skyraScript.setAttribute('data-testid', 'skyra-smart-script');
    skyraScript.onload = start;
    document.body.appendChild(skyraScript);
    function start() {
      const windowWithSkyra = window; // vi vet at skyra er lastet inn

      windowWithSkyra.skyra?.start({
        org: 'skatteetaten'
      });
      if (sporTilMatomo) {
        sendDataTilMatomo(windowWithSkyra);
      }
    }
  }
  function sendDataTilMatomo(windowWithSkyra) {
    windowWithSkyra.skyra?.events.on('next-card', ({
      card,
      submittedValue
    }) => {
      if (!isSessionIdDimensionSetRef.current) {
        sendSessionIdTilMatomo();
      }

      // Sett brukeroppgave
      if (card.type === 'TopTaskCard') {
        const selectedTaskId = submittedValue;
        const selectedTask = card.taskItems.find(taskItem => taskItem.task.id === selectedTaskId);
        if (selectedTask) {
          setCustomDimensionIHelhetligMåling(_constants.MATOMO_CUSTOM_DIMENSION_IDS.BRUKEROPPGAVE, selectedTask.label);
        }
      }

      // Sett fikk til oppgaven ja/nei
      if (card.type === 'CompletionCard') {
        const fikkDetTil = submittedValue === true ? 'Ja' : 'Nei';
        setCustomDimensionIHelhetligMåling(_constants.MATOMO_CUSTOM_DIMENSION_IDS.FIKK_DET_TIL, fikkDetTil);
      }
    });
  }
  function sendSessionIdTilMatomo() {
    const skyraCookie = (0, _utils.getCookieVerdi)('skyra.state');
    if (!skyraCookie) return;
    try {
      const skyraCookieObj = JSON.parse(skyraCookie);
      const activeSurvey = Object.values(skyraCookieObj).find(survey => survey?.state === 'Capture');
      if (activeSurvey) {
        setCustomDimensionIHelhetligMåling(_constants.MATOMO_CUSTOM_DIMENSION_IDS.SKYRA_SESSION_ID, activeSurvey.sessionId);
        isSessionIdDimensionSetRef.current = true;
      }
    } catch (error) {
      console.error('Ygyldig Skyra cookie:', error);
    }
  }
  function setCustomDimensionIHelhetligMåling(dimensionId, value) {
    leggTilInstruksjon(function () {
      if (this.getSiteId() === _constants.SKATTEETATEN_SITE_ID) {
        this.setCustomDimension(dimensionId, value);
      }
    });
  }
  return {
    startSkyraSmartScript
  };
}